import { Collapse, Form } from 'antd';
import './RightInsideCustomDesignMenu.scss';
import InputComponent from '../../common/InputComponent/InputComponent';
import { Icon } from '../../icon/icon';
import { useContext, useEffect, useState } from 'react';
import { CabinetEditorContext } from '../CabinetEditor/CabinetEditor';
import RightMenuDesign from '../../house/RightMenuDesign/RightMenuDesign';
import Chip from '../../common/Chip/Chip';
import ButtonPrimarySmall from '../../common/ButtonPrimary/ButtonPrimarySmall';
import { GeometryUtils } from '../../../utils/geometry/GeometryUtils';
import { message } from 'antd';
import { getInputType } from '../../../utils/string/inputExpression';
import { openMessageError } from '../../common/Message/openMessageError';
import { openMessageWarning } from '../../common/Message/openMessageWarning';
import { openMessageSuccess } from '../../common/Message/openMessageSuccess';
import { InsideType } from '../../../constants/enum';
import InsideCabinetHandler from '../../../three/core/handlers/InsideCabinetHandler';
import { useTranslation } from 'react-i18next';

const RightInsideCustomDesignMenu = ({ open, form, className }) => {
  const { t } = useTranslation();
  const cabinetEditorContext = useContext(CabinetEditorContext);
  const insideType = cabinetEditorContext.insideType;
  const [duplicateCount, setDuplicateCount] = useState(1);
  const { insideLoaderRef, sceneManager, setTick, meshClickRef } =
    cabinetEditorContext;

  // const [formInside, setFormInside] = useState([]);

  useEffect(() => {
    if (open && sceneManager) {
      if (meshClickRef?.current) {
        const foundModule =
          sceneManager.cabinetManager.currentCabinet.getCloneModule();

        let size = { x: 0, y: 0, z: 0 };
        if (foundModule && meshClickRef.current) {
          size = GeometryUtils.getSizeRotation(
            foundModule.group,
            meshClickRef.current
          );
        }

        form.setFieldsValue({
          widthDrawer: Math.round(size.z * 1000),
          heightDrawer: Math.round(size.y * 1000),
          depthDrawer: Math.round(size.x * 1000),
        });
      }
    }
  }, [open, sceneManager, meshClickRef]);

  const handleChangeDrawerHeight = async newHeight => {
    if (!insideLoaderRef.current || !sceneManager || !meshClickRef.current) {
      openMessageError({ content: t("Can't change height") });
      return;
    }

    // Validation
    if (newHeight < 17) {
      openMessageWarning({
        content: t('Minimum height is {{x}}mm', { x: 17 }),
      });
      return;
    }

    try {
      const foundModule =
        sceneManager.cabinetManager.currentCabinet.getCloneModule();

      if (!foundModule) {
        openMessageError({ content: t('Module not found') });
        return;
      }

      // Hiển thị loading
      const hide = message.loading(t('Updating height'), 0);
      InsideCabinetHandler.scaleHeight(
        meshClickRef.current,
        (newHeight - 8) / 1000
      );

      hide();

      const newSize = GeometryUtils.getSizeRotation(
        foundModule.group,
        meshClickRef.current
      );

      form.setFieldsValue({
        widthDrawer: Math.round(newSize.z * 1000),
        heightDrawer: Math.round(newSize.y * 1000),
        depthDrawer: Math.round(newSize.x * 1000),
      });

      setTick(tick => tick + 1);
      openMessageSuccess({ content: t('Height updated successfully') });
    } catch (error) {
      console.error('Error changing U-Drawer height:', error);
      openMessageError({
        content: t("Can't change height") + ': ' + error.message,
      });
    }
  };

  const handleDuplicateEvenly = async () => {
    if (!insideLoaderRef.current || !sceneManager || !meshClickRef.current) {
      message.error(t("Can't duplicate"));
      return;
    }

    // Validation số lượng
    if (!duplicateCount || duplicateCount < 1) {
      message.warning(t('Quantity must be greater than 0'));
      return;
    }

    if (duplicateCount > 20) {
      message.warning(t('Maximum quantity is 20'));
      return;
    }

    try {
      const hide = message.loading(t('Duplicating'), 0);

      const foundModule =
        sceneManager.cabinetManager.currentCabinet.getCloneModule();

      const currentCabinet = sceneManager?.cabinetManager?.getCurrentCabinet();

      if (!foundModule) {
        hide();
        message.error(t('Module not found'));
        return;
      }

      // Xác định insideType
      let currentInsideType = insideType;

      // Nếu không có insideType từ context, lấy từ mesh
      if (!currentInsideType && meshClickRef.current?.userData?.type) {
        currentInsideType = meshClickRef.current.userData.type;
      }

      // Kiểm tra xem có hỗ trợ nhân bản không
      const supportedTypes = [
        InsideType.DRAWER,
        InsideType.HPANEL,
        InsideType.VPANEL,
        InsideType.VPANEL2,
      ];
      if (!supportedTypes.includes(currentInsideType)) {
        hide();
        message.warning(t('This element type does not support duplication'));
        return;
      }

      // Gọi hàm nhân bản đều
      await insideLoaderRef.current.duplicateEvenly(
        meshClickRef.current,
        duplicateCount,
        currentInsideType,
        foundModule,
        currentCabinet
      );

      hide();
      setTick(tick => tick + 1); // Update scene
      message.success(t('Duplicated {{x}} elements', { x: duplicateCount }));
    } catch (error) {
      console.error('Error duplicating:', error);
      message.error(t("Can't duplicate") + ': ' + error.message);
    }
  };

  const formForCurrentCabinet = {
    partitionPlate: [
      {
        key: 'horizontalBoard1',
        label: <span className="text-title-medium">{t('Size')}</span>,
        children: (
          <div className="flex flex-col gap-16 items-start self-stretch">
            <Form.Item name={'width'} label={t('Width')}>
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
                disabled={true}
              />
            </Form.Item>
            <Form.Item name={'depth'} label={t('Depth')}>
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
                disabled={true}
              />
            </Form.Item>
            <Form.Item name={'height'} label={t('Height')}>
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
                disabled={true}
              />
            </Form.Item>
          </div>
        ),
      },
    ],
    drawer: [
      {
        key: 'drawer1',
        label: <span className="text-title-medium">{t('Size')}</span>,
        children: (
          <div className="flex flex-col gap-16 items-start self-stretch">
            <Form.Item name={'widthDrawer'} label={t('Width')}>
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
                disabled={true}
              />
            </Form.Item>
            <Form.Item name={'depthDrawer'} label={t('Depth')}>
              <div className="flex items-center gap-4">
                <InputComponent
                  addonAfter={<span className="text-body-medium">mm</span>}
                  placeholder={t('Enter')}
                />
              </div>
            </Form.Item>
            <Form.Item name={'heightDrawer'} label={t('Height')}>
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
                onChange={e => {
                  if (getInputType(e.target.value) === 'number') {
                    const newValue = parseInt(e.target.value);
                    if (!isNaN(newValue) && newValue >= 17) {
                      // Debounce để tránh gọi quá nhiều lần
                      if (window.drawerHeight) {
                        clearTimeout(window.drawerHeight);
                      }
                      window.drawerHeight = setTimeout(() => {
                        handleChangeDrawerHeight(newValue);
                      }, 500); // Đợi 500ms sau khi user ngừng nhập
                    }
                  }
                }}
              />
            </Form.Item>
          </div>
        ),
      },
      {
        key: 'drawer2',
        label: <span className="text-title-medium">{t('Layout')}</span>,
        children: (
          <div className="flex flex-col gap-16 items-start self-stretch">
            <Form.Item label={t('Top edge distance')}>
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>
            <Form.Item label={t('Bottom edge distance')}>
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>
          </div>
        ),
      },
    ],
    uDrawer: [
      {
        key: 'uDrawer1',
        label: <span className="text-title-medium">{t('Size')}</span>,
        children: (
          <div className="flex flex-col gap-16 items-start self-stretch">
            <Form.Item label={t('Length')}>
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
                disabled={true}
              />
            </Form.Item>
            <Form.Item label={t('Depth')}>
              <div className="flex items-center gap-4">
                <InputComponent
                  addonAfter={<span className="text-body-medium">mm</span>}
                  placeholder={t('Enter')}
                />
              </div>
            </Form.Item>
            <Form.Item label={t('Height')}>
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>
          </div>
        ),
      },
      {
        key: 'uDrawer2',
        label: <span className="text-title-medium">{t('Drawer')}</span>,
        children: (
          <div className="flex flex-col gap-16 items-end self-stretch">
            <div className="flex flex-col items-start gap-12 self-stretch">
              <span className="text-label-medium" style={{ color: '#808B90' }}>
                {t('Drawer layout')}
              </span>
              <div className="flex items-start align-content-start gap-8 self-stretch flex-wrap">
                <Chip
                  posIcon={
                    <span className="flex w-18 h-18">
                      <Icon.ICLayout1 />
                    </span>
                  }
                />
                <Chip
                  posIcon={
                    <span className="flex w-18 h-18">
                      <Icon.ICLayout2 />
                    </span>
                  }
                />
                <Chip
                  posIcon={
                    <span className="flex w-18 h-18">
                      <Icon.ICLayout3 />
                    </span>
                  }
                />
                <Chip
                  posIcon={
                    <span className="flex w-18 h-18">
                      <Icon.ICLayout12 />
                    </span>
                  }
                />
                <Chip
                  posIcon={
                    <span className="flex w-18 h-18">
                      <Icon.ICLayout22 />
                    </span>
                  }
                />
                <Chip
                  posIcon={
                    <span className="flex w-18 h-18">
                      <Icon.ICLayout23 />
                    </span>
                  }
                />
                <Chip
                  posIcon={
                    <span className="flex w-18 h-18">
                      <Icon.ICLayout13 />
                    </span>
                  }
                />
                <Chip
                  posIcon={
                    <span className="flex w-18 h-18">
                      <Icon.ICLayout23 />
                    </span>
                  }
                />
                <Chip
                  posIcon={
                    <span className="flex w-18 h-18">
                      <Icon.ICLayout33 />
                    </span>
                  }
                />
              </div>
            </div>
            <div
              className="flex p-8 flex-col items-start gap-12 self-stretch rounded-[4px]"
              style={{ background: '#F5F6F6' }}
            >
              <div className="text-label-medium" style={{ color: '#808B90' }}>
                {t('Drawer height')}
              </div>
              <div className="flex items-center gap-16 self-stretch flex-col">
                <Form.Item
                  label={
                    <span className="text-label-medium">
                      {t('Drawer level')} 3
                    </span>
                  }
                >
                  <InputComponent
                    addonAfter={<span className="text-body-medium">mm</span>}
                    placeholder={t('Enter')}
                  />
                </Form.Item>
                <Form.Item
                  label={
                    <span className="text-label-medium">
                      {t('Drawer level')} 2
                    </span>
                  }
                >
                  <InputComponent
                    addonAfter={<span className="text-body-medium">mm</span>}
                    placeholder={t('Enter')}
                  />
                </Form.Item>
                <Form.Item
                  label={
                    <span className="text-label-medium">
                      {t('Drawer level')} 1
                    </span>
                  }
                >
                  <InputComponent
                    addonAfter={<span className="text-body-medium">mm</span>}
                    placeholder={t('Enter')}
                  />
                </Form.Item>
              </div>
            </div>
            <ButtonPrimarySmall title={t('Apply')} />
          </div>
        ),
      },
    ],
  };

  // useEffect(() => {
  //   form.setFieldsValue({
  //     width: cabinetEditorContext.mdCustomData.mdDimensions.width * 1000,
  //     height: cabinetEditorContext.mdCustomData.mdDimensions.height * 1000,
  //     depth: cabinetEditorContext.mdCustomData.mdDimensions.depth * 1000,
  //   });
  // }, [open]);

  return (
    <RightMenuDesign
      open={open}
      classNames={{
        root: 'top-16 ' + className,
        content: '!p-0 gap-0',
      }}
      header={
        <span className="text-title-large text-nowrap">{t('Structure')}</span>
      }
      content={
        <>
          <Form
            form={form}
            labelAlign="left"
            labelWrap
            labelCol={{ flex: '100px' }}
            colon={false}
            layout="horizontal"
            validateTrigger={'onChange'}
            className="left-cabinet-design-menu-body flex flex-col items-start overflow-hidden p-8 gap-8 grow self-stretch"
          >
            <Collapse
              expandIcon={({ isActive }) => (
                <span className="w-16 h-16 flex">
                  {isActive ? <Icon.ICExpandMore /> : <Icon.ICExpandLess />}
                </span>
              )}
              defaultActiveKey={[
                'drawer1',
                'drawer2',
                'uDrawer1',
                'uDrawer2',
                'duplicate',
                'horizontalBoard1',
              ]}
              onChange={() => {}}
              className="self-stretch overflow-auto"
              items={[
                ...([0, 1, 11].includes(insideType)
                  ? formForCurrentCabinet.partitionPlate
                  : [3].includes(insideType)
                    ? formForCurrentCabinet.drawer
                    : []),
                {
                  key: 'duplicate',
                  label: (
                    <span className="text-title-medium">
                      {t('Duplicate evenly')}
                    </span>
                  ),
                  children: (
                    <div className="flex flex-col gap-16 items-end self-stretch">
                      <Form.Item label={t('Quantity')}>
                        <InputComponent
                          placeholder={t('Enter')}
                          type="number"
                          value={duplicateCount}
                          onChange={e => {
                            const val = parseInt(e.target.value);
                            setDuplicateCount(val);
                          }}
                        />
                      </Form.Item>
                      <ButtonPrimarySmall
                        title={t('Apply')}
                        onClick={handleDuplicateEvenly}
                      />
                    </div>
                  ),
                },
              ].map(item => ({
                ...item,
                onKeyDown: e => {
                  e.stopPropagation();
                },
              }))}
            />
          </Form>
        </>
      }
    />
  );
};

export default RightInsideCustomDesignMenu;
